import { LEGAL_KEYS, type LegalKey, type LegalLocale, type LegalPage, type LegalPagesConfig } from './types'

/**
 * The legal section's URLs — the same on every site, and the ONLY place they
 * are spelled. Slash-terminated: the build writes `<path>index.html` and
 * nginx serves the directory form. VV2's route table reads these too.
 *
 * Pure module: no DOM, no React (vite.config.ts imports it).
 */
export const LEGAL_PATHS: Record<LegalKey, Record<LegalLocale, string>> = {
  privacy: { it: '/privacy/', en: '/en/privacy/' },
  cookie: { it: '/cookie-policy/', en: '/en/cookie-policy/' },
  dataDeletion: { it: '/data-deletion/', en: '/en/data-deletion/' },
  crediti: { it: '/crediti/', en: '/en/credits/' },
}

export function legalPathFor(key: LegalKey, locale: LegalLocale): string {
  return LEGAL_PATHS[key][locale]
}

/** Query and fragment dropped, duplicate slashes collapsed, leading + trailing slash. */
export function normalizeLegalPath(pathname: string): string {
  const bare = pathname.split(/[?#]/, 1)[0]
  let p = '/' + bare.replace(/^\/+/, '').replace(/\/{2,}/g, '/')
  if (!p.endsWith('/')) p += '/'
  return p
}

/** Every page a site gets, in tab order then locale order; none when it has not opted in. */
export function listLegalPages(config: Pick<LegalPagesConfig, 'locales'> | undefined): LegalPage[] {
  if (!config) return []
  return LEGAL_KEYS.flatMap((key) => config.locales.map((locale) => ({ key, locale, path: LEGAL_PATHS[key][locale] })))
}

export function matchLegalPath(
  pathname: string,
  config: Pick<LegalPagesConfig, 'locales'> | undefined,
): LegalPage | null {
  const p = normalizeLegalPath(pathname)
  return listLegalPages(config).find((page) => page.path === p) ?? null
}
